/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Dropdown, PasswordInput } from '@carbon/react';
import { InlineNotification } from 'carbon-components-react';
import _ from 'lodash';
import { useEffect, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { useDelayDebounce } from '../../../../actions/hooks/useDelayDebounce';
import { selectIsElevenlabsEnabled } from '../../../../ia/store/features/selectors';
import { getBranding } from '../../../../ia/util/branding';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { useTextToSpeech } from '../../../hooks/useSpeechCommon';
import {
  shouldHideInstancePicker,
  selectTTSInstanceCreateRequired,
  selectPhoneIntegrationType
} from '../../../store/phone/selectors';
import {
  createTextToSpeechInstance,
  loadElevenLabsModels,
  loadElevenLabsVoices
} from '../../../store/voice/actions';
import {
  selectCurrentTextToSpeechInstance,
  selectTextToSpeechCreateStatus,
  selectCustomVoices,
  selectTextToSpeechInstanceLoaded,
  selectDefaultTextToSpeechModel,
  selectElevenLabsModels,
  selectElevenLabsModelsErrors,
  selectElevenLabsVoices
} from '../../../store/voice/selectors';
import { GENESYS_AUDIO_CONNECTOR_TYPE } from '../../../util/channelTypes';
import {
  TTS_PROVIDER_ELEVENLABS_DEFAULT_MODEL_ID,
  TTS_PROVIDER_ELEVENLABS_DEFAULT_VOICE_NAME,
  TTS_PROVIDER_ELEVENLABS_ID,
  TTS_PROVIDER_WATSON_ID
} from '../constants';
import { Speech } from '../messages';
import { FIELDS } from '../schema';
import { ControlledDropdownSelector } from '../speech-common/ControlledDropdownSelector';
import { CreateSpeechInstanceButton } from '../speech-common/CreateSpeechInstanceButton';
import { SpeechInstanceTable } from '../speech-common/SpeechInstanceTable';
import { TextToSpeech as messages } from './messages';
import { Voices } from './Voices';
import { VoicesElevenLabs } from './VoicesElevenLabs';

interface TextToSpeechProps {
  size?: string;
  readOnly?: boolean;
}
export const TextToSpeech = ({ size = 'lg', readOnly }: TextToSpeechProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue, registerInputField } =
    useFormHelpers(formMethods);

  const speechProviders = [
    {
      id: TTS_PROVIDER_WATSON_ID,
      text: t(messages.ttsProviderWatson)
    },
    {
      id: TTS_PROVIDER_ELEVENLABS_ID,
      text: t(messages.ttsProviderElevenLabs)
    }
  ];

  const contactCenterProvider = useAppSelector(selectPhoneIntegrationType);
  const is11labsEnabled = useAppSelector(selectIsElevenlabsEnabled);
  const models = useAppSelector(selectElevenLabsModels);
  const apiKeyError = useAppSelector(selectElevenLabsModelsErrors);
  const voices = useAppSelector(selectElevenLabsVoices);

  const isGenesysAudioConnectorSelected =
    contactCenterProvider === GENESYS_AUDIO_CONNECTOR_TYPE;

  const [hasCreated, setHasCreated] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState(undefined);
  const [selectedVoice, setSelectedVoice] = useState(undefined);

  const ttsVoice = getFieldValue(FIELDS.TTS.VOICE);
  const provider = getFieldValue(FIELDS.TTS.PROVIDER);
  const modelId = getFieldValue(FIELDS.TTS.MODEL_ID);
  const voice = getFieldValue(FIELDS.TTS.VOICE);

  // Debounce ElevenceAPIKey to delay API call until blur
  const debouncedElevenceAPIKey = useDelayDebounce(
    getFieldValue(FIELDS.TTS.ELEVENLABS_APIKEY),
    1000
  );

  // When Genesys audio conector is selected and is11labsEnabled feature flag is on and no provider selected
  // Default to eleven_labs
  useEffect(() => {
    if (isGenesysAudioConnectorSelected && is11labsEnabled && !provider) {
      updateFieldValue(FIELDS.TTS.PROVIDER, TTS_PROVIDER_ELEVENLABS_ID);
    }
  }, [
    is11labsEnabled,
    isGenesysAudioConnectorSelected,
    provider,
    updateFieldValue
  ]);

  useEffect(() => {
    // on every api key change, fetch models
    if (debouncedElevenceAPIKey) {
      dispatch(loadElevenLabsModels(debouncedElevenceAPIKey));
    }
  }, [debouncedElevenceAPIKey, dispatch]);

  useEffect(() => {
    // When models are loaded, and apikey present fetch voices
    if (models?.length && debouncedElevenceAPIKey) {
      dispatch(loadElevenLabsVoices(debouncedElevenceAPIKey));
    }
  }, [debouncedElevenceAPIKey, dispatch, models?.length]);

  // UseEffect to determine selected Model (quick setup and edit flow)
  useEffect(() => {
    if (
      provider === TTS_PROVIDER_ELEVENLABS_ID &&
      selectedModel === undefined &&
      models?.length
    ) {
      const selModel = models.find(model =>
        modelId
          ? model.model_id === modelId
          : model.model_id === TTS_PROVIDER_ELEVENLABS_DEFAULT_MODEL_ID
      );
      if (selModel) {
        setSelectedModel(selModel);
        updateFieldValue(FIELDS.TTS.MODEL_ID, selModel.model_id);
      }
    }
  }, [modelId, models, provider, selectedModel, updateFieldValue]);

  // UseEffect to determine selected voice (quick setup and edit flow)
  useEffect(() => {
    if (
      provider === TTS_PROVIDER_ELEVENLABS_ID &&
      selectedVoice === undefined &&
      voices?.length
    ) {
      // In Edit flow, if voice found return voice based on previously selected voice
      const selVoice = voices.find(vo =>
        voice
          ? vo.name === voice
          : vo.name === TTS_PROVIDER_ELEVENLABS_DEFAULT_VOICE_NAME
      );

      if (selVoice) {
        setSelectedVoice(selVoice);
        updateFieldValue(FIELDS.TTS.VOICE, selVoice?.name);
        updateFieldValue(FIELDS.TTS.VOICE_ID, selVoice?.voice_id);
      }
    }
  }, [provider, selectedVoice, updateFieldValue, voice, voices]);

  const customVoices = useAppSelector(state => {
    return selectCustomVoices(state);
  });
  const isTtsLoaded = useAppSelector(selectTextToSpeechInstanceLoaded);
  const currentTTSInstance = useAppSelector(selectCurrentTextToSpeechInstance);
  const TTSInstanceCreateRequired = useAppSelector(
    selectTTSInstanceCreateRequired
  );
  const TTSDefaultModel = useAppSelector(selectDefaultTextToSpeechModel);

  // Set the field value based on selection
  // Compare against original to determine if dirty
  const handleSelection = useCallback(
    selectedItem => {
      if (!selectedItem.customization_id) {
        updateFieldValue(FIELDS.TTS.CUSTOMIZATION_ID, '');
      } else {
        // const dirty =
        //   selectedItem.customization_id !==
        //   getFieldValue(FIELDS.TTS.CUSTOMIZATION_ID);
        updateFieldValue(
          FIELDS.TTS.CUSTOMIZATION_ID,
          selectedItem.customization_id
        );
      }
    },
    [updateFieldValue]
  );

  // Use the speechCommon
  const {
    showPremiumWarning,
    instances,
    handleSelect,
    createError,
    loadingInstances
  } = useTextToSpeech();

  const { productName } = getBranding();

  useEffect(() => {
    if (hasCreated && currentTTSInstance && TTSDefaultModel) {
      updateFieldValue(FIELDS.TTS.INSTANCE_ID, currentTTSInstance?.id);
      updateFieldValue(FIELDS.TTS.URL, currentTTSInstance?.credentials?.url);
      updateFieldValue(
        FIELDS.TTS.APIKEY,
        currentTTSInstance?.credentials?.apikey
      );
      updateFieldValue(FIELDS.TTS.VOICE, TTSDefaultModel);
    }
  }, [hasCreated, currentTTSInstance, updateFieldValue, TTSDefaultModel]);

  useEffect(() => {
    // If we have a tts model but no custom and custom is set, unset it...
    if (
      ttsVoice &&
      customVoices?.length === 0 &&
      getFieldValue(FIELDS.TTS.CUSTOMIZATION_ID)
    ) {
      updateFieldValue(FIELDS.TTS.CUSTOMIZATION_ID, '');
    }
    // If we have a tts model but no custom and custom is set, unset it...
  }, [customVoices, getFieldValue, updateFieldValue, ttsVoice]);

  const onChangeSpeechProvider = item => {
    updateFieldValue(FIELDS.TTS.PROVIDER, item.selectedItem.id);
    // Reset voice since both providers uses same voice field and that can lead to issues
    updateFieldValue(FIELDS.TTS.VOICE, '');
  };

  const onChangeSelectedVoice = item => {
    setSelectedVoice(item.selectedItem);
    updateFieldValue(FIELDS.TTS.VOICE, item.selectedItem.name);
    // Set voice_id only for 11Labs provider
    if (provider === TTS_PROVIDER_ELEVENLABS_ID) {
      updateFieldValue(FIELDS.TTS.VOICE_ID, item.selectedItem.voice_id);
    }
  };

  // onChangeModel is used for 11Labs
  const onChangeModel = item => {
    const newSelectedModel = models.filter(
      model => model.model_id === item.selectedItem.model_id
    );
    setSelectedModel(newSelectedModel[0]);
    updateFieldValue(FIELDS.TTS.MODEL_ID, newSelectedModel[0]?.model_id);
  };

  // configureVoiceSection is for Watson TTS provider
  const configureVoiceSection = (
    <div className="TabContent__body-content">
      <Voices id="voice-selector" />
      {size === 'lg' && customVoices?.length > 0 && (
        <>
          <div className="TextToSpeech__field-container">
            <h5>
              <FormattedMessage {...messages.ttsCustomVoiceLabel} />
            </h5>
            <div className="TabContent__spacer8" />
            <div className="Dropdown__filtered-selector">
              <ControlledDropdownSelector
                customizationIdField={true}
                id="custom-voices"
                instanceLoaded={isTtsLoaded}
                enableNone={true}
                labelText={t(messages.voiceModel)}
                field={FIELDS.TTS.CUSTOMIZATION_ID}
                items={customVoices}
                onChange={handleSelection}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Prevent the instance selection picker from being shown in AWS specifically;
  // it should, however, be shown on IBM Cloud (whether standalone or under WXO)
  if (shouldHideInstancePicker()) {
    return configureVoiceSection;
  }

  const renderWatsonTTSSection = (
    <>
      <div className="TabContent__body-content">
        <>
          <h4 className="productive-heading-02">
            <FormattedMessage {...messages.ttsInstanceLabel} />
          </h4>
          <div className="TabContent__spacer8" />
          <p className="TabContent__subtitle">
            <FormattedMessage {...messages.instanceDescription} />
          </p>
        </>
      </div>
      {!loadingInstances && showPremiumWarning && (
        <InlineNotification
          kind="warning"
          title=""
          className="Phone__Speech-PremiumSpeechWarning"
          subtitle={t(Speech.entDataIsolWarning)}
          iconDescription="close warning"
          statusIconDescription="close warning"
        />
      )}
      {currentTTSInstance?.id && (
        <>
          <SpeechInstanceTable
            readOnly={readOnly}
            instances={instances}
            defaultSelection={currentTTSInstance?.id}
            handleSelect={handleSelect}
            loading={loadingInstances}
            size="sm"
          />
          <div className="TabContent__spacer8" />
          {configureVoiceSection}
        </>
      )}
    </>
  );

  const render11LabsSection = (
    <>
      <div className="TabContent__body-content">
        <PasswordInput
          {...registerInputField({
            field: FIELDS.TTS.ELEVENLABS_APIKEY,
            placeholderText: t(messages.apiKey),
            readOnly: false,
            labelText: t(messages.elevenLabsApiKey),
            isPasswordField: true,
            id: 'apiKeyId',
            invalid: Boolean(apiKeyError),
            invalidText: apiKeyError
          })}
          className="TextToSpeech__provider-password"
        />
        <div className="TabContent__spacer16" />
        {debouncedElevenceAPIKey &&
          models?.length > 0 &&
          selectedModel &&
          Object.keys(selectedModel).length > 0 && (
            <>
              <Dropdown
                label={t(messages.model)}
                titleText={t(messages.model)}
                className="TextToSpeech__provider-dropdown"
                size="md"
                id="model"
                items={models}
                selectedItem={selectedModel}
                itemToString={item => (item ? item.name : '')}
                onChange={onChangeModel}
              />
              <div className="TabContent__spacer8" />

              {voices?.length > 0 && (
                <Dropdown
                  label={'Select Voice'}
                  titleText={t(messages.voice)}
                  className="TextToSpeech__provider-dropdown"
                  size="md"
                  id="language"
                  items={voices}
                  selectedItem={selectedVoice}
                  itemToString={item => (item ? item.name : '')}
                  onChange={onChangeSelectedVoice}
                  direction={'top'}
                />
              )}
              <VoicesElevenLabs selectedVoiceId={selectedVoice?.voice_id} />
            </>
          )}

        <div className="TabContent__spacer8" />
      </div>
    </>
  );

  return (
    <>
      {TTSInstanceCreateRequired ? (
        <div className="TabContent__body-content">
          <p>
            <FormattedMessage
              {...messages.createMessage}
              values={{ productName }}
            />
          </p>
          {createError && (
            <InlineNotification
              kind="warning"
              title={_.get(createError, 'error') ?? 'Failure'}
              subtitle={_.get(createError, 'trace') ?? createError}
              hideCloseButton={true}
            />
          )}
          <div className="TabContent__spacer24">
            <CreateSpeechInstanceButton
              statusSelector={selectTextToSpeechCreateStatus}
              create={async () => {
                await dispatch(createTextToSpeechInstance());
                setHasCreated(true);
              }}
            />
          </div>
        </div>
      ) : (
        <>
          {isGenesysAudioConnectorSelected && is11labsEnabled ? (
            <>
              {/*------Common section for both Speech providers----start*/}
              <h4 className="productive-heading-02">
                {t(messages.configureVoiceTitleElevenLabs)}
              </h4>
              <div className="TabContent__spacer8" />
              <p className="TabContent__subtitle">
                {t(messages.configureVoiceDescriptionElevenLabs)}
              </p>
              <div className="TabContent__spacer8" />

              <Dropdown
                titleText={t(messages.ttsProvider)}
                label={t(messages.selectTTSProvider)}
                className="TextToSpeech__provider-dropdown"
                id="stt-speech-provider-dropdown"
                items={speechProviders}
                selectedItem={speechProviders.find(
                  item => item.id === provider
                )}
                itemToString={item => (item ? item.text : '')}
                onChange={onChangeSpeechProvider}
              />
              {/*------Common section for both Speech providers-----ends*/}
              {provider === speechProviders[0].id && renderWatsonTTSSection}
              {provider === speechProviders[1].id && render11LabsSection}
            </>
          ) : (
            renderWatsonTTSSection
          )}
        </>
      )}
    </>
  );
};
